import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColorOrString, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import React from 'react'

import { ChevronDownIcon } from '../../icons'
import Typography from './typography'

type AccordionVariant = 'primary' | 'secondary' | 'outline'

interface AccordionProps {
  id?: string
  title: string | React.ReactNode
  subtitle?: React.ReactNode
  children: React.ReactNode
  open?: boolean
  borderless?: boolean
  className?: string
  variant?: AccordionVariant
  display?: boolean
  toggleRounded?: boolean
  onToggle?: () => void
}

const Accordion: React.FC<AccordionProps> = ({
  id,
  title,
  subtitle,
  children,
  open = false,
  display = true,
  borderless = false,
  className,
  variant = 'secondary',
  toggleRounded = false,
  onToggle
}) => {
  const chevronColor: Record<AccordionVariant, TailwindColorOrString> = {
    primary: tailwindColors.white.DEFAULT,
    secondary: tailwindColors.secondary.DEFAULT,
    outline: tailwindColors.primary.DEFAULT
  }

  if (!display) return null

  return (
    <div
      className={cn(
        'border rounded-2xl border-grey-light p-5',
        { 'border-0': borderless },
        className
      )}
      id={id}
    >
      <div className="flex justify-between items-center gap-3 w-full">
        <button
          type="button"
          onClick={onToggle}
          className="flex justify-between items-center flex-1 min-w-0 focus:outline-none"
        >
          <div className="flex items-center gap-2 min-w-0">
            {typeof title === 'string' ? (
              <Typography variant="sub-heading" className="text-grey">
                {title}
              </Typography>
            ) : (
              title
            )}
          </div>
          <div
            className={cn('rounded p-2', {
              'bg-primary': variant === 'primary',
              'rounded-full': toggleRounded,
              'bg-secondary-light': variant === 'secondary',
              'bg-white-hover rounded-full border border-grey-light': variant === 'outline'
            })}
          >
            <ChevronDownIcon
              className={cn('h-4 w-4 shrink-0 transition-transform duration-200', {
                'rotate-180': open
              })}
              color={chevronColor[variant]}
            />
          </div>
        </button>
        {subtitle && <div className="flex items-center gap-2 shrink-0">{subtitle}</div>}
      </div>
      <div
        className={cn('transition-all duration-300', {
          'max-h-fit overflow-visible': open,
          'max-h-0 overflow-hidden': !open
        })}
      >
        <div className="h-4" />
        {children}
      </div>
    </div>
  )
}

export default Accordion
